import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useFormik } from 'formik';
import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { getAllProjects } from '../../../services/projectService';
import { getSectionsByProjectId } from '../../../services/sectionService';
import { createTask, getAllTasksByProjectId } from '../../../services/taskService';
import { INITIAL_DIALOG_STATE } from '../constants/defaultValues';
import AddTaskSchema from '../schema/addTaskSchema';

const AddTaskModal = ({ open, defaultTaskName = '', onClose, fetchTaskData }) => {
  const [sectionList, setSectionList] = useState([]);
  const [projectList, setProjectList] = useState([]);
  const [parentTask, setParentTask] = useState([]);

  const accountData = useSelector((state) => state?.login?.user || null);

  const handleAddTask = async (values) => {
    try {
      const data = {
        projectId: values?.project?.id,
        sectionId: values?.section?.id,
        userId: accountData?.id,
        taskName: values?.title?.trim(),
        plannedHours: values?.plannedHours,
        taskType: values?.taskType,
        parentTaskId: values?.parentTaskId,
        issueType: values?.issueType,
        startDate: values?.startDate,
        endDate: values?.endDate,
      };

      if (values?.taskType !== 'Issue') {
        delete data.parentTaskId;
        delete data.issueType;
      }

      await createTask(data);
      toast.success('Task Added successfully');
      fetchTaskData();
      onClose();
    } catch (error) {
      toast.error('An error occurred. Please try again later.');
    }
  };

  const formik = useFormik({
    initialValues: INITIAL_DIALOG_STATE,
    validationSchema: AddTaskSchema,
    onSubmit: handleAddTask,
  });

  const { values, setFieldValue, getFieldProps, touched, errors, handleSubmit, isSubmitting } = formik;

  const fetchAllProjectTasks = async (id) => {
    try {
      const taskListResponse = await getAllTasksByProjectId({
        projectId: id,
        pageSize: 99999,
        wantCompletedTask: true,
      });
      setParentTask(taskListResponse?.data);
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || 'Something went wrong while fetching tasks. Please try again later';
      toast.error(errorMessage);
    }
  };

  const fetchSections = async (id) => {
    try {
      const response = await getSectionsByProjectId(id);
      setSectionList(response?.data?.data?.rows);
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || 'Something went wrong while fetching sections. Please try again later';
      toast.error(errorMessage);
    }
  };

  const handleProjectChange = async (event, value) => {
    setFieldValue('project', value);
    if (value?.id) {
      fetchAllProjectTasks(value?.id);
      fetchSections(value?.id);
    }
  };

  const handleSectionChange = (event, value) => {
    setFieldValue('section', value);
  };

  useEffect(() => {
    setFieldValue('title', defaultTaskName || '', false);
    return () => {
      formik.resetForm();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const fetchProjectData = async () => {
      try {
        const Projects = await getAllProjects({ status: 'active', allowManualTask: '1', isMemberOnly: true });
        setProjectList(Projects?.data?.data?.rows);
      } catch (error) {
        toast.error(error?.response?.data?.message);
      }
    };

    fetchProjectData();
  }, []);

  const taskTypes = [
    { label: 'Task', value: 'Task' },
    // { label: 'Issue', value: 'Issue' },
    { label: 'Change Request', value: 'CR' },
  ];

  return (
    <Dialog open={open} fullWidth maxWidth={'xs'} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Add a new task</DialogTitle>
        <DialogContent>
          <TextField
            name="title"
            fullWidth
            autoFocus
            margin="dense"
            id="name"
            {...getFieldProps('title')}
            label="Task Name"
            type="text"
            variant="standard"
            error={Boolean(touched.title && errors.title)}
            helperText={touched.title && errors.title}
          />
          <TextField
            name="plannedHours"
            fullWidth
            margin="dense"
            id="plannedHours"
            {...getFieldProps('plannedHours')}
            label="Planned Hours"
            type="number"
            variant="standard"
            autoComplete="off"
            error={Boolean(touched.plannedHours && errors.plannedHours)}
            helperText={touched.plannedHours && errors.plannedHours}
          />

          <Autocomplete
            sx={{ mt: 1 }}
            margin="dense"
            options={projectList}
            groupBy={(option) => option.clients.clientName}
            getOptionLabel={(option) => option.projectName}
            value={values.project}
            onChange={handleProjectChange}
            id="projectId"
            autoComplete
            includeInputInList
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select project"
                variant="standard"
                error={Boolean(touched.project && errors.project)}
                helperText={touched.project && errors.project}
              />
            )}
          />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Autocomplete
                sx={{ mt: 2 }}
                margin="dense"
                options={sectionList}
                getOptionLabel={(option) => option.name}
                value={values.section}
                onChange={handleSectionChange}
                id="sectionId"
                autoComplete
                includeInputInList
                disabled={!values?.project}
                title={values?.project ? 'Select section' : 'Please select a project first'}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select section"
                    variant="standard"
                    error={Boolean(touched.section && errors.section)}
                    helperText={touched.section && errors.section}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                sx={{ mt: 2 }}
                value={values.taskType}
                margin="dense"
                defaultValue={taskTypes[0]}
                options={taskTypes}
                onChange={(event, value) => setFieldValue('taskType', value.value)}
                id="taskId"
                includeInputInList
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Task Type"
                    variant="standard"
                    error={Boolean(touched.taskType && errors.taskType)}
                    helperText={touched.taskType && errors.taskType}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Grid item xs={6}>
                <DatePicker
                  label="Start Date"
                  value={formik.values.startDate}
                  onChange={(newValue) => setFieldValue('startDate', newValue)}
                  inputFormat="DD-MMM-YYYY"
                  disablePast
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      {...getFieldProps('startDate')}
                      error={touched.startDate && Boolean(errors.startDate)}
                      helperText={touched.startDate && errors.startDate}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <DatePicker
                  label="End Date"
                  value={formik.values.endDate}
                  onChange={(newValue) => setFieldValue('endDate', newValue)}
                  inputFormat="DD-MMM-YYYY"
                  disablePast
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      {...getFieldProps('endDate')}
                      error={touched.endDate && Boolean(errors.endDate)}
                      helperText={touched.endDate && errors.endDate}
                    />
                  )}
                />
              </Grid>
            </LocalizationProvider>
          </Grid>
          {values?.taskType === 'Issue' && (
            <>
              <Autocomplete
                sx={{ mt: 2 }}
                fullWidth
                options={['High', 'Medium', 'Low']}
                value={values.issueType}
                onChange={(event, value) => setFieldValue('issueType', value)}
                id="issueType"
                includeInputInList
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Issue Priority"
                    variant="standard"
                    error={Boolean(touched.issueType && errors.issueType)}
                    helperText={touched.issueType && errors.issueType}
                  />
                )}
              />

              <Autocomplete
                sx={{ mt: 2 }}
                fullWidth
                options={parentTask?.rows} // Assuming parentTask contains an object with a 'rows' property containing the array of parent tasks
                getOptionLabel={(option) => option?.taskName}
                filterOptions={(options, { inputValue }) => {
                  if (!Array.isArray(options)) {
                    return [];
                  }
                  const inputValueLowerCase = inputValue.toLowerCase();
                  return options.filter(
                    (option) =>
                      option?.id.toString().includes(inputValueLowerCase) ||
                      option?.taskName.toLowerCase().includes(inputValueLowerCase)
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Parent Task Name"
                    type="text"
                    variant="standard"
                    error={Boolean(touched.parentTaskId && errors.parentTaskId)}
                    helperText={touched.parentTaskId && errors.parentTaskId}
                  />
                )}
                onChange={(event, selectedOption) => {
                  setFieldValue('parentTaskId', selectedOption?.id ? parseInt(selectedOption.id, 10) : undefined);
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <LoadingButton type="submit" loading={isSubmitting}>
            Add
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

AddTaskModal.propTypes = {
  open: PropTypes.bool.isRequired,
  defaultTaskName: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  fetchTaskData: PropTypes.func.isRequired,
};

export default AddTaskModal;
